const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function fetchAPI(url: string, options: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export type PeerReviewStatus = 'pending' | 'in_review' | 'completed';

export interface PeerReviewCriterion {
  name: string;
  description: string;
  maxScore: number;
}

export interface PeerReview {
  id: number;
  rating: number;
  feedback: string;
  criteria?: Record<string, number>;
  createdAt: string;
}

export interface PeerReviewAttachment {
  filename: string;
  mimeType: string;
  size: number;
}

export interface PeerReviewAssignment {
  id: number;
  lessonId: number;
  lessonTitle: string;
  courseTitle?: string;
  submitterUserId: number;
  reviewerUserId: number;
  status: PeerReviewStatus;
  assignedAt: string;
  completedAt?: string;
  submission?: string;
  peerReviewCriteria?: PeerReviewCriterion[];
  reviews?: PeerReview[];
  attachment?: PeerReviewAttachment;
}

export interface SubmitForReviewResponse {
  success: boolean;
  message: string;
  assignmentCount: number;
  assignments: PeerReviewAssignment[];
}

export interface ReceivedReviewsResponse {
  reviews: PeerReview[];
  averageRating: number | null;
  reviewCount: number;
}

export interface SubmitReviewData {
  rating: number;
  feedback: string;
  criteria?: Record<string, number>;
}

class PeerReviewService {
  /**
   * Submit work for peer review
   */
  async submitForReview(lessonId: number, submission: string): Promise<SubmitForReviewResponse> {
    return fetchAPI(`${API_BASE_URL}/api/peer-review/submit`, {
      method: 'POST',
      body: JSON.stringify({ lessonId, submission }),
    });
  }

  /**
   * Get pending reviews assigned to current user
   */
  async getPendingReviews(): Promise<PeerReviewAssignment[]> {
    return fetchAPI(`${API_BASE_URL}/api/peer-review/pending`);
  }

  /**
   * Get my submissions and their reviews
   */
  async getMySubmissions(): Promise<PeerReviewAssignment[]> {
    return fetchAPI(`${API_BASE_URL}/api/peer-review/submissions`);
  }

  /**
   * Get assignment details
   */
  async getAssignment(id: number): Promise<PeerReviewAssignment> {
    return fetchAPI(`${API_BASE_URL}/api/peer-review/${id}`);
  }

  /**
   * Start reviewing an assignment
   */
  async startReview(assignmentId: number): Promise<{ success: boolean; message: string }> {
    return fetchAPI(`${API_BASE_URL}/api/peer-review/${assignmentId}/start`, {
      method: 'POST',
    });
  }

  /**
   * Submit a review
   */
  async submitReview(assignmentId: number, data: SubmitReviewData): Promise<{ success: boolean; message: string; review: PeerReview }> {
    return fetchAPI(`${API_BASE_URL}/api/peer-review/${assignmentId}/review`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get reviews received for a lesson
   */
  async getReceivedReviews(lessonId: number): Promise<ReceivedReviewsResponse> {
    return fetchAPI(`${API_BASE_URL}/api/peer-review/lesson/${lessonId}/reviews`);
  }

  /**
   * Submit work for peer review with optional file attachment
   */
  async submitForReviewWithFile(
    lessonId: number,
    submission: string,
    file?: File
  ): Promise<SubmitForReviewResponse> {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('lessonId', lessonId.toString());
    formData.append('submission', submission);

    if (file) {
      formData.append('file', file);
    }

    const response = await fetch(`${API_BASE_URL}/api/peer-review/submit-with-file`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get attachment download URL
   */
  getAttachmentUrl(assignmentId: number): string {
    return `${API_BASE_URL}/api/peer-review/${assignmentId}/attachment`;
  }

  /**
   * Get my submission for a specific lesson (for course player)
   */
  async getMySubmissionForLesson(lessonId: number): Promise<{
    assignmentId: number;
    submission: string;
    attachmentFilename?: string;
    status: string;
    editable: boolean;
    createdAt: string;
  } | null> {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/api/peer-review/lesson/${lessonId}/my-submission`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Accept': 'application/json',
      },
    });
    if (response.status === 204) return null;
    if (!response.ok) throw new Error('Failed to load submission');
    return response.json();
  }

  /**
   * Update an existing submission (only if no reviews completed yet)
   */
  async updateSubmission(lessonId: number, submission: string): Promise<{ success: boolean; message: string }> {
    return fetchAPI(`${API_BASE_URL}/api/peer-review/lesson/${lessonId}/update-submission`, {
      method: 'PUT',
      body: JSON.stringify({ submission }),
    });
  }

  /**
   * Download attachment
   */
  async downloadAttachment(assignmentId: number): Promise<void> {
    const token = localStorage.getItem('token');
    const response = await fetch(this.getAttachmentUrl(assignmentId), {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download attachment');
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'attachment';

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const peerReviewService = new PeerReviewService();
export default peerReviewService;
